import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, Shield, Wrench, Clock, DollarSign } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function EstevanReplacement() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does roof replacement cost in Estevan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A standard roof replacement in Estevan generally ranges from $6,000 to $14,000. Factors include the size, pitch, and materials. We provide honest, fixed-price quotes so you know exactly what to expect.",
        },
      },
      {
        "@type": "Question",
        name: "How long does it take to replace a roof in Estevan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most residential roof replacements in Estevan are completed in 1 to 2 days, weather permitting. We work efficiently to minimize disruption to your home.",
        },
      },
      {
        "@type": "Question",
        name: "Why is proper ventilation important for my new roof?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "In southern Saskatchewan, extreme temperature swings can cause condensation in your attic. Proper ventilation prevents winter ice dams and summer heat buildup, extending the life of your new roof.",
        },
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": "https://www.saskroofingcompany.com/#localbusiness",
    name: "Sask Roofing Company",
    url: "https://www.saskroofingcompany.com/",
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: "Estevan",
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roof Replacement",
    provider: {
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Estevan",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.saskroofingcompany.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: "https://www.saskroofingcompany.com/areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Estevan",
        item: "https://www.saskroofingcompany.com/areas/estevan",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Replacement",
        item: "https://www.saskroofingcompany.com/areas/estevan/replacement",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Roof Replacement Estevan | Sask Roofing Company"
        description="Expert roof replacement in Estevan, SK. We install premium, wind-resistant roofs built for southern Saskatchewan weather. Get an honest, fixed-price quote."
        schema={[
          faqSchema,
          localBusinessSchema,
          serviceSchema,
          breadcrumbSchema,
        ]}
      />
      <div className="flex flex-col min-h-screen">
        <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/6e2ca68f-b65c-4e53-8f1f-3c188b15b51c.png&output=webp&q=60"
              alt="Estevan Roof Replacement"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Roof Replacement Estevan
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                When your Estevan home needs a new roof, trust the local experts
                who understand southern Saskatchewan weather. We install premium
                roofing systems designed to withstand high winds, intense sun,
                and heavy snow. Honest pricing, local crews, and zero surprises.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">Get Your Free Inspection</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  When to Replace Your Estevan Roof
                </h2>
                <p className="text-lg text-muted-foreground">
                  Estevan's climate is harsh. The intense summer sun breaks down
                  asphalt, while winter freeze-thaw cycles pry shingles apart.
                  If you ignore the warning signs of a failing roof, you risk
                  severe water damage to your home's interior and structure.
                </p>
                <div className="space-y-4 pt-4">
                  <h3 className="text-xl font-semibold">
                    Look for these common signs:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Clock className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Age:</strong> Your roof is nearing 20 years old
                        and showing generalized wear.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Shield className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Curling & Cracking:</strong> Shingles are
                        visibly distorted, brittle, or missing entirely after a
                        storm.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Wrench className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Granule Loss:</strong> Excessive shingle
                        granules washing into your eavestroughs, leaving the
                        protective layer compromised.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <DollarSign className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Active Leaks:</strong> Water stains on your
                        ceilings or walls, indicating the roofing system has
                        failed.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/40828d16-935f-4087-8b3c-2dd81ee3ce2a.png&output=webp&q=60"
                  alt="Roof installation process in Estevan"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
              <h2 className="text-3xl font-bold">
                Built for Estevan Roof Replacement Saskatchewan
              </h2>
              <p className="text-lg text-muted-foreground">
                We don't just nail down shingles; we build complete roofing
                systems engineered for the extreme weather of the southeast
                prairies.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">High-Wind Resistance</h3>
                <p className="text-muted-foreground">
                  Our architectural shingles are rated for high wind speeds.
                  Combined with enhanced nailing techniques, your roof stays
                  intact during severe prairie storms.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">
                  Ice Damming Prevention
                </h3>
                <p className="text-muted-foreground">
                  We install premium ice and water shields along all eaves and
                  valleys to protect against the freeze-thaw cycles that plague
                  Estevan winters.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Advanced Ventilation</h3>
                <p className="text-muted-foreground">
                  Proper airflow is critical. We ensure your attic breathes
                  correctly, extending the life of your roof, reducing summer
                  cooling costs, and preventing winter condensation.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Building Permits and Code in Estevan
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Navigating local building codes can be confusing, but we
                  handle it for you. In Estevan, a standard residential roof
                  replacement (where we are simply tearing off old shingles and
                  installing new ones over existing decking) typically does not
                  require a building permit. However, if your roof has suffered
                  structural damage—such as broken trusses from heavy snow loads
                  or rotted decking that needs extensive replacement—a permit
                  from the City of Estevan may be required. We assess your
                  roof's structural integrity during our free inspection and
                  manage all necessary permitting and compliance, ensuring your
                  new roof meets all local and provincial building codes.
                </p>
              </div>
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Insurance and Storm Claims in Estevan
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Estevan is no stranger to severe summer hailstorms and high
                  wind events that can devastate a roof in minutes. If your roof
                  has been damaged by a storm, dealing with insurance can be
                  stressful. We specialize in storm damage restoration and
                  frequently work with local insurance adjusters. If you suspect
                  damage, call us for a free inspection before filing a claim.
                  We will document the damage with detailed photos and provide a
                  comprehensive report for your adjuster, ensuring you get the
                  full coverage you are entitled to for a proper roof
                  replacement.
                </p>
              </div>
            </div>

            <div className="mb-20">
              <div className="bg-card p-8 rounded-xl border shadow-sm max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4 text-center">
                  Materials and Warranties for Estevan Roofs
                </h3>
                <p className="text-muted-foreground leading-relaxed text-center mb-6">
                  For Estevan's demanding climate, we typically recommend
                  premium architectural shingles from trusted manufacturers like
                  IKO, CertainTeed, or GAF. Specifically, we often suggest Class
                  4 impact-resistant shingles to combat the frequent hailstorms
                  in the region. These shingles are engineered to withstand
                  severe weather and come with robust manufacturer warranties.
                  When combined with our local workmanship warranty, you get a
                  roofing system that provides long-lasting peace of mind
                  against the harsh Saskatchewan elements.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100+
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Roofs in Estevan
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Class 4
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Impact Resistance
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      130km/h
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Wind Rating
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100%
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Local Crews
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/40828d16-935f-4087-8b3c-2dd81ee3ce2a.png&output=webp&q=60"
                  alt="Estevan roofing crew"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 className="text-3xl font-bold">
                  Our Estevan Roof Replacement Process
                </h2>
                <p className="text-lg text-muted-foreground">
                  We believe in transparency and doing the job right the first
                  time. Here is what you can expect when you hire Sask Roofing
                  Company.
                </p>
                <ol className="space-y-6 pt-4">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Comprehensive Inspection
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We assess your entire roofing system, including the
                        deck, flashing, and ventilation, to identify all
                        underlying issues.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Fixed-Price Quote
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        You receive a detailed, written estimate. What you see
                        is what you pay—no hidden fees or surprise upcharges.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Tear-Off & Installation
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        Our local crew removes your old roof down to the deck,
                        inspects the wood, and installs your new premium roofing
                        system.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Immaculate Clean-Up
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We protect your landscaping during the build and perform
                        a thorough magnetic sweep to ensure no stray nails are
                        left behind.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-muted/10 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label="Estevan roofing pages"
              className="bg-background p-6 rounded-xl border shadow-sm"
            >
              <p className="font-bold text-lg mb-4 text-foreground">
                More roofing services in Estevan:
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/areas/estevan"
                    className="text-primary hover:underline font-medium"
                  >
                    ← All Estevan roofing services (overview)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas/estevan/repair"
                    className="text-primary hover:underline font-medium"
                  >
                    Roof Repair in Estevan
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    All Sask roofing services
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <section className="py-20 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Estevan Roof Replacement FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-background rounded-xl border p-2 shadow-sm"
            >
              <AccordionItem value="item-1" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How much does roof replacement cost in Estevan?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  A standard roof replacement in Estevan generally ranges from
                  $6,000 to $14,000. Factors include the size, pitch, and
                  materials. We provide honest, fixed-price quotes so you know
                  exactly what to expect.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How long does it take to replace a roof in Estevan?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Most residential roof replacements in Estevan are completed in
                  1 to 2 days, weather permitting. We work efficiently to
                  minimize disruption to your home.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Why is proper ventilation important for my new roof?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  In southern Saskatchewan, extreme temperature swings can cause
                  condensation in your attic. Proper ventilation prevents winter
                  ice dams and summer heat buildup, extending the life of your
                  new roof.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Ready to Protect Your Estevan Home?
            </h2>
            <p className="text-xl opacity-90">
              Get a free, no-pressure inspection and a fixed-price quote from
              Saskatchewan's trusted roofing experts.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Get Your Free Roof Inspection</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
